import { Environment } from '@cobliteam/react-environments';
import { SessionManager } from '../../account/session/SessionManager';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { IRequest } from '../../request/Types';
import type { DebounceConfig, DebounceDefault, WebhooksAddContent, WebhooksUpdateContent, WebhookTestResponseBody } from './Definitions/Types';
import { WEBHOOK_TEST_UPSTREAM_UNREACHABLE } from './Definitions/Types';
import type { WebhookEventTypesResponse } from './Definitions/WebhookTypes';
import { webhooksTransformer } from './WebhooksTransformers';

const env = Environment.getInstance().getEnvironment();

export class WebhooksRequester {
  private requester: Requester;

  constructor() {
    const sessionManager = new SessionManager({ host: env.experience });
    this.requester = new Requester({ host: env.experience }, sessionManager);
  }

  public getWebhooks = (): Promise<WebhooksUpdateContent[]> => {
    const requestData: IRequest = {
      endpoint: '/v1/signatures',
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => webhooksTransformer(response.data));
  };

  public generateNewWebhook = (webhookInfo: WebhooksAddContent): Promise<string> => {
    const requestData: IRequest = {
      endpoint: '/v1/signatures',
      method: HttpMethod.POST,
      body: JSON.stringify({
        name: webhookInfo.name,
        event_types: webhookInfo.eventTypes,
        secret_key: webhookInfo.secretKey,
        url: webhookInfo.url,
        custom_headers: webhookInfo.customHeaders,
      }),
    };

    return this.requester.request(requestData).then((response) => response.data.id);
  };

  public updateWebhook = (webhookInfo: WebhooksUpdateContent): Promise<string> => {
    const requestData: IRequest = {
      endpoint: `/v1/signatures/${webhookInfo.id}`,
      method: HttpMethod.PATCH,
      body: JSON.stringify({
        id: webhookInfo.id,
        name: webhookInfo.name,
        event_types: webhookInfo.eventTypes,
        secret_key: webhookInfo.secretKey,
        url: webhookInfo.url,
        custom_headers: webhookInfo.customHeaders,
      }),
    };

    return this.requester.request(requestData).then((response) => response.data);
  };

  public revokeWebhooks = (webhookId: string): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/v1/signatures/${webhookId}`,
      method: HttpMethod.DELETE,
    };

    return this.requester.request(requestData).then();
  };

  public reactivateWebhook = (webhookId: string): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/v1/signatures/${webhookId}/reactivate`,
      method: HttpMethod.PATCH,
    };

    return this.requester.request(requestData).then();
  };

  public getEventTypes = (): Promise<WebhookEventTypesResponse> => {
    const requestData: IRequest = {
      endpoint: '/v1/signatures/event-types',
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => {
      const telemetry = Array.isArray(response?.data?.telemetry) ? response.data.telemetry : [];
      const camera = Array.isArray(response?.data?.camera) ? response.data.camera : [];
      const microconfig = Array.isArray(response?.data?.microconfig) ? response.data.microconfig : [];
      const routes = Array.isArray(response?.data?.routes) ? response.data.routes : [];
      const activities = Array.isArray(response?.data?.activities) ? response.data.activities : [];
      return { telemetry, camera, microconfig, routes, activities };
    });
  };

  public testWebhooks = (webhookInfo: WebhooksAddContent): Promise<boolean | string> => {
    const requestData: IRequest = {
      endpoint: 'https://api.cobli.co/public/v1/signatures/test',
      method: HttpMethod.POST,
      body: JSON.stringify({
        url: webhookInfo.url,
        headers: webhookInfo.customHeaders,
        secret_key: webhookInfo.secretKey,
      }),
    };

    return this.requester
      .request(requestData)
      .then((response) => {
        // iris ADR-0006: newer iris answers 200 with success:false when the
        // customer upstream fails; older iris mirrors the upstream status and
        // is handled by the catch below. A string return means failure.
        const body = response.data as WebhookTestResponseBody | string | null | undefined;
        if (body && typeof body === 'object' && body.success === false) {
          if (body.error === WEBHOOK_TEST_UPSTREAM_UNREACHABLE) {
            return 'upstream unreachable: status code 504';
          }
          return `upstream error: status code ${body.upstream_status ?? 500}`;
        }
        return response.status >= 200 && response.status < 300;
      })
      .catch((err) => {
        return err.message;
      });
  };
  public getDebounceDefaults = (): Promise<DebounceDefault[]> => {
    const requestData: IRequest = {
      endpoint: '/v1/debounce-defaults',
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then(
      (response) =>
        ((response.data?.data ?? response.data) as Array<{ event_type: string; debounce_mode: string; debounce_ms: number; min_count?: number | null }>).map((item) => ({
          eventType: item.event_type,
          debounceMode: item.debounce_mode,
          debounceMs: item.debounce_ms,
          minCount: item.min_count ?? undefined,
        })) as DebounceDefault[]
    );
  };

  public getSignatureDebounce = (signatureId: string): Promise<DebounceConfig[]> => {
    const requestData: IRequest = {
      endpoint: `/v1/signatures/${signatureId}/debounce`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then(
      (response) =>
        (
          (response.data?.data ?? response.data) as Array<{
            key?: { event_type: string };
            event_type?: string;
            debounce_mode: string;
            debounce_ms: number;
            min_count?: number | null;
          }>
        ).map((item) => ({
          eventType: (item.key?.event_type ?? item.event_type ?? '').toLowerCase(),
          debounceMode: item.debounce_mode,
          debounceMs: item.debounce_ms,
          minCount: item.min_count ?? undefined,
        })) as DebounceConfig[]
    );
  };

  public upsertSignatureDebounce = (signatureId: string, eventType: string, config: Pick<DebounceConfig, 'debounceMode' | 'debounceMs' | 'minCount'>): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/v1/signatures/${signatureId}/debounce/${eventType}`,
      method: HttpMethod.PUT,
      body: JSON.stringify({
        debounce_mode: config.debounceMode,
        debounce_ms: config.debounceMs,
        min_count: config.minCount,
      }),
    };

    return this.requester.request(requestData).then();
  };

  public deleteSignatureDebounce = (signatureId: string, eventType: string): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/v1/signatures/${signatureId}/debounce/${eventType}`,
      method: HttpMethod.DELETE,
    };

    return this.requester.request(requestData).then();
  };
}

export default WebhooksRequester;
